import * as React from "react"
import { MoreVertical, LogOut } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

export interface SidebarUserProfileProps {
  user: {
    name: string
    email: string
    avatarUrl?: string
  }
  onLogout?: () => void
  isCollapsed?: boolean
  isAMLAnalyst?: boolean
  onToggleUserRole?: () => void
}

export function SidebarUserProfileComponent({ user, onLogout, isCollapsed = false, isAMLAnalyst = false, onToggleUserRole }: SidebarUserProfileProps) {
  const [confirmSwitchUserOpen, setConfirmSwitchUserOpen] = React.useState(false)
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const formatDisplayName = (name: string) => {
    const parts = name.split(" ")
    if (parts.length === 1) return name
    const firstName = parts[0]
    const lastInitial = parts[parts.length - 1][0]
    return `${firstName} ${lastInitial}.`
  }

  const handleRoleSwitch = () => {
    setConfirmSwitchUserOpen(false)
    onToggleUserRole?.()
  }

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.metaKey && event.key === 'l') {
        event.preventDefault()
        onLogout?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onLogout])

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex w-full items-center gap-3 rounded-md px-3 py-2 transition-colors duration-150 hover:bg-bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-text-subtle focus-visible:ring-offset-2 focus-visible:ring-offset-bg-subtle cursor-pointer ${isCollapsed ? 'justify-center' : 'md:justify-start justify-center'}`}>
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.avatarUrl} alt={user.name} />
            <AvatarFallback className="bg-bg-emphasized text-xs text-text-default">
              {getInitials(user.name)}
            </AvatarFallback>
          </Avatar>
          <div className={`hidden flex-1 flex-col items-start justify-center ${!isCollapsed ? 'md:flex' : ''}`}>
            <span className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-default">
              {formatDisplayName(user.name)}
            </span>
          </div>
          <MoreVertical className={`hidden h-5 w-5 text-text-default cursor-pointer ${!isCollapsed ? 'md:block' : ''}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="right"
        alignOffset={0}
        sideOffset={8}
        className="w-auto rounded-lg border border-border-default bg-background-general-card p-1 shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10)]"
      >
        <DropdownMenuItem
          onClick={() => isAMLAnalyst && setConfirmSwitchUserOpen(true)}
          className={`min-h-12 rounded-md px-2 py-2 ${!isAMLAnalyst ? 'bg-bg-emphasized' : 'cursor-pointer hover:bg-bg-emphasized focus:bg-bg-emphasized'}`}
        >
          <div className="flex flex-1 items-center justify-start gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-sm text-white bg-base-purple-50">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-default">
                Banker
              </span>
              <span className="text-xs text-text-subtlest">
                {user.email}
              </span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => !isAMLAnalyst && setConfirmSwitchUserOpen(true)}
          className={`min-h-12 rounded-md px-2 py-2 ${isAMLAnalyst ? 'bg-bg-emphasized' : 'cursor-pointer hover:bg-bg-emphasized focus:bg-bg-emphasized'}`}
        >
          <div className="flex flex-1 items-center justify-start gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-sm text-white bg-base-purple-50">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-default">
                AML Analyst
              </span>
              <span className="text-xs text-text-subtlest">
                {user.email}
              </span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={onLogout}
          className="min-h-8 cursor-pointer rounded-md px-2 py-[5.50px] hover:bg-bg-emphasized focus:bg-bg-emphasized"
        >
          <div className="flex flex-1 items-center justify-start gap-3">
            <LogOut className="h-5 w-5 text-text-default" />
            <span className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-default">
              Log out
            </span>
          </div>
          <span className="text-sm font-normal leading-[21px] text-text-subtlest">
            ⇧⌘Q
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <AlertDialog open={confirmSwitchUserOpen} onOpenChange={setConfirmSwitchUserOpen}>
      <AlertDialogContent className="max-w-[360px] rounded-xl border-0 bg-background-general-card p-6">
        <AlertDialogHeader className="space-y-2">
          <AlertDialogTitle className="text-center text-lg font-medium text-text-default">
            Are you sure you want to switch profiles?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center text-sm text-muted-foreground">
            Permissions and access levels will change based on the selected account. Please review before continuing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col mt-4">
          <AlertDialogAction asChild onClick={handleRoleSwitch}>
            <Button variant="default" className="w-full">
              Yes
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild onClick={() => setConfirmSwitchUserOpen(false)}>
            <Button variant="outline" className="w-full">
              No
            </Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
